import { contactFieldsOptions } from '../contact-fields-options';

import type { FieldValue, FieldValues, StepConfig, StepFormField } from '../../types/workflow';

const maxValues = {
  seconds: 86400,
  minutes: 1440,
  hours: 24,
};

const isWhatsAppList = (parentValues: Record<string, any>) => {
  return parentValues?.question?.type == 'whatsapp_list';
};

const askQuestion: StepConfig = {
  // Design
  icon: 'askQuestion',
  headerTextColor: '#00BCD4',
  headerColor: '#E6F7FA',
  title: 'Ask a Question',
  text: '',
  category: 'Router',
  desc: "Allows a specific question to be sent to the contact on the last interacted channel. If the last interacted channel doesn't exist or the message fails to be sent in general, the contact will exit the workflow.",
  primaryKey: (values: FieldValues): string => {
    const questionData =
      values.question && typeof values.question === 'object' ? (values.question as Record<string, any>) : {};

    return String(questionData?.content || '');
  },
  // Variable fields configuration
  variableFields: [
    {
      fieldKey: 'saveResponse.variable',
      isArray: false,
    },
  ],
  // Connector configuration
  connectors: {
    ValidAnswer: {
      headerTextColor: '#4CAF50',
      headerColor: '#E8F5E9',
      title: 'Valid Answer',
      category: 'Connector',
    },
    InvalidAnswer: {
      headerTextColor: '#F44336',
      headerColor: '#FFEBEE',
      title: 'Invalid Answer',
      category: 'Connector',
    },
    Timeout: {
      headerTextColor: '#00BCD4',
      headerColor: '#E6F7FA',
      title: 'Failure:Timeout',
      category: 'Connector',
      show: (nodeData: Record<string, any>) => {
        // Check if timeout is enabled through either hasTimeout flag or enable toggle
        return nodeData.timeout?.hasTimeout === true;
      },
    },
  },
  // Form
  formFields: [
    {
      key: 'question.type',
      label: 'Question Type',
      type: 'select',
      defaultValue: 'text',
      returnObject: false,
      options: [
        { label: 'Text', value: 'text' },
        { label: 'Multiple Choice', value: 'options' },
        { label: 'WhatsApp List', value: 'whatsapp_list' },
        { label: 'Attachment', value: 'attachment' },
        { label: 'Number', value: 'number' },
        { label: 'Email', value: 'email' },
        { label: 'URL', value: 'url' },
        { label: 'Location', value: 'location_request_message' },
      ],
      onChange: (value: FieldValue, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
        if (allFields['question.content']) {
          allFields['question.content'].show = value !== 'whatsapp_list';
        }
        if (allFields['question.multipleOptions']) {
          allFields['question.multipleOptions'].show = value === 'options';
        }
        if (allFields['question.whatsappListItems']) {
          allFields['question.whatsappListItems'].show = value === 'whatsapp_list';
        }
      },
    },
    {
      key: 'question.content',
      label: 'Question Text',
      placeholder: 'Leave Your question Here ...',
      type: 'textarea',
      defaultValue: '',
      show: (parentValues: Record<string, any>) => !isWhatsAppList(parentValues),
      validation: {
        required: true,
      },
    },
    {
      key: 'question.multipleOptions',
      label: 'Choices',
      type: 'formRepeater',
      desc: 'Maximum of 10 choices can be added',
      defaultValue: [''],
      dependsOn: ['question.type'],
      show: (parentValues: Record<string, any>) => {
        return parentValues.question.type === 'options';
      },
      returnObject: false,
      config: {
        maxItems: 10,
        minItems: 1,
        addButtonText: 'Add Choice',
        fields: [
          {
            key: 'choice',
            type: 'text',
            placeholder: 'Choice',
            validation: {
              required: true,
              maxLength: 20,
            },
          },
        ],
      },
    },
    {
      key: 'question.header',
      label: 'Header',
      placeholder: 'Header',
      type: 'text',
      defaultValue: '',
      show: isWhatsAppList,
      validation: {
        maxLength: 60,
      },
    },
    {
      key: 'question.body',
      label: 'Body',
      placeholder: 'Body',
      type: 'textarea',
      defaultValue: '',
      show: isWhatsAppList,
      validation: {
        required: true,
        maxLength: 1024,
      },
    },
    {
      key: 'question.footer',
      label: 'Footer',
      placeholder: 'Footer',
      type: 'text',
      defaultValue: '',
      show: isWhatsAppList,
      validation: {
        maxLength: 60,
      },
    },
    {
      key: 'question.listName',
      label: 'List Name',
      placeholder: 'List Name',
      type: 'text',
      defaultValue: '',
      show: isWhatsAppList,
      validation: {
        required: true,
        maxLength: 20,
      },
    },
    {
      key: 'question.whatsappListItems',
      label: 'Items',
      type: 'formRepeater',
      desc: 'You can add up to 10 items.',
      defaultValue: [],
      dependsOn: ['question.type'],
      show: isWhatsAppList,
      validation: {
        required: true,
      },
      returnObject: true,
      config: {
        maxItems: 10,
        minItems: 0,
        addButtonText: 'Add Item',
        maxItemsMessage: 'You can add up to 10 items.',
        autoIncrementFieldKey: 'title',
        autoIncrementPrefix: 'Item',
        fields: [
          {
            key: 'title',
            type: 'text',
            placeholder: 'Item title',
            validation: {
              maxLength: 24,
              required: true,
            },
          },
          {
            key: 'description',
            placeholder: 'Description',
            type: 'text',
            validation: {
              maxLength: 72,
            },
          },
        ],
      },
    },
    {
      key: 'saveResponseAs',
      label: 'Save Response As',
      type: 'accordion',
      defaultValue: false,
      fields: [
        {
          key: 'saveResponse.hasField',
          label: 'Contact Field',
          type: 'toggle',
          defaultValue: false,
          onChange: (value, allValues, allFields) => {
            if (value) {
              allFields['saveResponse.field'].show = true;
            } else {
              allFields['saveResponse.field'].show = false;
            }
          },
        },
        // options of contacts if saveAsContactField is true
        {
          key: 'saveResponse.field',
          type: 'select',
          searchKey: 'filter[q]',
          show: false,
          options: contactFieldsOptions,
        },
        {
          key: 'saveResponse.hasVariable',
          label: 'Variable',
          type: 'toggle',
          defaultValue: false,
          onChange: (value, allValues, allFields) => {
            if (value) {
              allFields['saveResponse.variable'].show = true;
            } else {
              allFields['saveResponse.variable'].show = false;
            }
          },
        },
        {
          key: 'saveResponse.variable',
          placeholder: 'Enter Variable Name',
          type: 'text',
          defaultValue: '',
          show: false,
        },
        {
          key: 'saveResponse.tag',
          label: 'Tag',
          type: 'toggle',
          defaultValue: false,
          show: false,
        },
      ],
    },
    {
      key: 'timeout.hasTimeout',
      label: 'Advanced Settings',
      type: 'accordion',
      defaultValue: false,
      fields: [
        {
          key: 'timeout.unit',
          label: 'Unit',
          type: 'select',
          defaultValue: 'minutes',
          options: [
            { label: 'Seconds', value: 'seconds' },
            { label: 'Minutes', value: 'minutes' },
            { label: 'Hours', value: 'hours' },
          ],
          dependsOn: ['timeout.hasTimeout'],
          onChange: (value, allValues, allFields) => {
            if (value === 'seconds') {
              if (allFields['timeout.value'] && allFields['timeout.value'].validation) {
                allFields['timeout.value'].validation.maxValue = maxValues.seconds;
              }
            } else if (value === 'minutes') {
              if (allFields['timeout.value'] && allFields['timeout.value'].validation) {
                allFields['timeout.value'].validation.maxValue = maxValues.minutes;
              }
            } else {
              if (allFields['timeout.value'] && allFields['timeout.value'].validation) {
                allFields['timeout.value'].validation.maxValue = maxValues.hours;
              }
            }
          },
        },
        {
          key: 'timeout.value',
          label: 'Timeout',
          type: 'number',
          defaultValue: 1,
          dependsOn: ['timeout.hasTimeout', 'timeout.unit'],
          validation: {
            required: true,
            numeric: true,
            integer: true,
            minValue: 1,
            maxValue: maxValues.minutes,
          },
        },
      ],
    },
  ],
};

export default askQuestion;
